import React, { useState, useEffect, useRef, useMemo } from "react";
import CustomMultiSelect from "../ui/CustomMultiSelect";
import CustomSelect from "../ui/CustomSelect";
import { ChevronDownIcon } from "../icons";
import { INVENTORY_ACTION_OPTIONS } from "../modals/editModalUtils";

const CONTRACT_ENDS_PRESET_VALUES = new Set(["", "30", "90", "180"]);
const CONTRACT_ENDS_WITHIN_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "180", label: "180 days" },
  { value: "custom", label: "Custom…" },
];
const CONTRACT_ENDS_MAX_DAYS = 3650;
const SEARCH_DEBOUNCE_MS = 350;

const daysUntilDate = (dateStr) => {
  if (!dateStr) return null;
  const end = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((end.getTime() - today.getTime()) / 86400000);
  if (diff < 1 || diff > CONTRACT_ENDS_MAX_DAYS) return null;
  return String(diff);
};

const dateFromDays = (daysStr) => {
  const days = Number.parseInt(daysStr, 10);
  if (!Number.isFinite(days) || days < 1) return "";
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Normalize filter value to array of non-empty strings
const toSelectedSet = (val) => {
  if (Array.isArray(val)) return new Set(val.filter((v) => v != null && String(v).trim() !== ""));
  if (val != null && String(val).trim() !== "") return new Set([String(val).trim()]);
  return new Set();
};

const FilterBar = ({
  filters,
  onFilterChange,
  inventory,
  providers = [],
  categories = [],
  serviceTypes = [],
  billingAccounts = [],
  billingAccountProviderMap = new Map(),
  filterCombinations = [],
  billingAccountsByProvider = [],
  categoriesByProvider = [],
  serviceTypesByProvider = [],
  siteFilterOptions = null,
  siteFilterOptionsLoading = false,
  shownCount,
  totalCount,
  sortConfig,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const [advancedSiteSearchOpen, setAdvancedSiteSearchOpen] = useState(() =>
    Boolean(
      filters.contractEndsWithin &&
        String(filters.contractEndsWithin).trim() !== "",
    ),
  );
  const contractEndsRaw = String(filters.contractEndsWithin ?? "").trim();
  const contractEndsIsCustom =
    contractEndsRaw !== "" && !CONTRACT_ENDS_PRESET_VALUES.has(contractEndsRaw);
  const [contractEndsCustomMode, setContractEndsCustomMode] = useState(
    () => contractEndsIsCustom,
  );
  const [customDaysDraft, setCustomDaysDraft] = useState(() =>
    contractEndsIsCustom ? contractEndsRaw : "",
  );
  const [customEndsByDraft, setCustomEndsByDraft] = useState(() =>
    contractEndsIsCustom ? dateFromDays(contractEndsRaw) : "",
  );
  const debounceRef = useRef(null);
  const customDaysDebounceRef = useRef(null);

  useEffect(() => {
    setSearchInput(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    if (
      filters.contractEndsWithin &&
      String(filters.contractEndsWithin).trim() !== ""
    ) {
      setAdvancedSiteSearchOpen(true);
    }
  }, [filters.contractEndsWithin]);

  // Sync drafts from an externally applied custom day count (e.g. restored filters).
  // Do NOT exit custom mode when the value happens to match a preset (30/90/180) —
  // that would flip "Custom…" back to the preset option while the user is typing.
  useEffect(() => {
    const raw = String(filters.contractEndsWithin ?? "").trim();
    if (!raw) return;
    if (CONTRACT_ENDS_PRESET_VALUES.has(raw) && !contractEndsCustomMode) return;
    if (!CONTRACT_ENDS_PRESET_VALUES.has(raw)) {
      setContractEndsCustomMode(true);
    }
    setCustomDaysDraft((prev) => {
      const p = prev.trim();
      if (!p || p === raw || p.startsWith(raw)) return prev || raw;
      return raw;
    });
    setCustomEndsByDraft((prev) => {
      const next = dateFromDays(raw);
      if (!next) return prev;
      if (!prev) return next;
      // Keep local date while days draft is still being typed past the applied value
      const draftDays = Number.parseInt(customDaysDraft, 10);
      if (
        Number.isFinite(draftDays) &&
        String(draftDays) !== raw &&
        String(customDaysDraft).trim().startsWith(raw)
      ) {
        return prev;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from filter value only
  }, [filters.contractEndsWithin]);

  const applyCustomContractDays = (daysStr) => {
    const days = Number.parseInt(String(daysStr).trim(), 10);
    if (
      !Number.isFinite(days) ||
      days < 1 ||
      days > CONTRACT_ENDS_MAX_DAYS
    ) {
      if (String(filters.contractEndsWithin ?? "").trim() !== "") {
        onFilterChange({ contractEndsWithin: "" });
      }
      return;
    }
    const next = String(days);
    if (String(filters.contractEndsWithin ?? "").trim() !== next) {
      onFilterChange({ contractEndsWithin: next });
    }
  };

  useEffect(() => {
    if (!contractEndsCustomMode) return undefined;
    if (customDaysDebounceRef.current) {
      clearTimeout(customDaysDebounceRef.current);
    }
    customDaysDebounceRef.current = setTimeout(() => {
      applyCustomContractDays(customDaysDraft);
      customDaysDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (customDaysDebounceRef.current) {
        clearTimeout(customDaysDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply on draft/mode only
  }, [customDaysDraft, contractEndsCustomMode]);

  const contractEndsSelectOptions = useMemo(() => {
    const customLabel =
      contractEndsCustomMode && customDaysDraft.trim()
        ? `Custom (${customDaysDraft.trim()} days)`
        : "Custom…";
    return CONTRACT_ENDS_WITHIN_OPTIONS.map((opt) =>
      opt.value === "custom" ? { ...opt, label: customLabel } : opt,
    );
  }, [contractEndsCustomMode, customDaysDraft]);

  const customInputClass =
    "h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--faint,#9a9a95)] outline-none focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/20 focus:border-[var(--line-strong,#d5d3cc)]";

  // Debounce only when the user edits the input. Do not depend on filters.search
  // here — an external prefill (e.g. from Invoices) updates filters first and
  // would otherwise schedule a push of the stale empty searchInput.
  useEffect(() => {
    if ((filters.search || "").trim() === (searchInput || "").trim()) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilterChange({ search: searchInput.trim() || "" });
      debounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit filters.search
  }, [searchInput, onFilterChange]);

  const handleSearchChange = (e) => setSearchInput(e.target.value);
  // Get selected values as arrays
  const getSelectedValues = (filterName) => {
    return Array.isArray(filters[filterName])
      ? filters[filterName]
      : filters[filterName]
      ? [filters[filterName]]
      : [];
  };

  // Helper: filter billing accounts by selected provider(s) using the map (works even when filterCombinations is empty)
  const filterBillingAccountsByProvider = (accountList, selectedProviderSet, map) => {
    if (!selectedProviderSet || selectedProviderSet.size === 0) return accountList;
    const normalize = (s) => String(s).trim();
    const normalizeNoSpaces = (s) => normalize(s).replace(/\s+/g, "").replace(/-/g, "");
    const selectedLower = new Set([...selectedProviderSet].map((p) => String(p).toLowerCase()));
    return accountList.filter((acc) => {
      const accStr = normalize(acc);
      const provider = map.get(accStr) || map.get(normalizeNoSpaces(accStr));
      return provider && selectedLower.has(String(provider).toLowerCase());
    });
  };

  // Case-insensitive set for provider matching (dropdown value may be "Nitel", DB may be "NITEL")
  const providerSetLower = (set) => new Set([...set].map((p) => String(p).toLowerCase()));

  // Get billing accounts for selected provider(s) from billingAccountsByProvider (backend source of truth)
  const getBillingAccountsForProviders = (providerSet, byProviderList) => {
    if (!providerSet || providerSet.size === 0 || !byProviderList || byProviderList.length === 0) return null;
    const providerSetLc = providerSetLower(providerSet);
    const pairs = byProviderList.filter(
      (p) => p && p.provider && p.billing_account != null && String(p.billing_account).trim() !== "" && providerSetLc.has(String(p.provider).trim().toLowerCase()),
    );
    const accounts = [...new Set(pairs.map((p) => String(p.billing_account).trim()))].sort();
    return accounts.length > 0 ? accounts : [];
  };

  // Get categories for selected provider(s) from categoriesByProvider
  const getCategoriesForProviders = (providerSet, byProviderList) => {
    if (!providerSet || providerSet.size === 0 || !byProviderList || byProviderList.length === 0) return null;
    const providerSetLc = providerSetLower(providerSet);
    const pairs = byProviderList.filter(
      (p) => p && p.provider && p.category != null && String(p.category).trim() !== "" && providerSetLc.has(String(p.provider).trim().toLowerCase()),
    );
    const vals = [...new Set(pairs.map((p) => String(p.category).trim()))].sort();
    return vals;
  };

  // Get service types for selected provider(s) from serviceTypesByProvider
  const getServiceTypesForProviders = (providerSet, byProviderList) => {
    if (!providerSet || providerSet.size === 0 || !byProviderList || byProviderList.length === 0) return null;
    const providerSetLc = providerSetLower(providerSet);
    const pairs = byProviderList.filter(
      (p) => p && p.provider && p.service_type != null && String(p.service_type).trim() !== "" && providerSetLc.has(String(p.provider).trim().toLowerCase()),
    );
    const vals = [...new Set(pairs.map((p) => String(p.service_type).trim()))].sort();
    return vals;
  };

  // Compute cascading options from filterCombinations based on current selections
  const cascadingOptions = useMemo(() => {
    const providerSet = toSelectedSet(filters.provider);
    const providerSetLc = providerSetLower(providerSet);
    const categorySet = toSelectedSet(filters.category);
    const serviceTypeSet = toSelectedSet(filters.serviceType);

    let combos = filterCombinations;
    const hasProviderFilter = providerSet.size > 0;

    // Billing accounts: prefer billingAccountsByProvider (every provider+billing_account pair from DB)
    let billingAccountOpts;
    const fromByProvider = getBillingAccountsForProviders(providerSet, billingAccountsByProvider);
    if (fromByProvider !== null && hasProviderFilter) {
      billingAccountOpts = fromByProvider;
    } else if (combos.length > 0 && hasProviderFilter) {
      const byProvider = combos.filter(
        (c) => c && c.provider && providerSetLc.has(String(c.provider).trim().toLowerCase()),
      );
      billingAccountOpts = [...new Set(byProvider.map((c) => c.billing_account).filter((v) => v != null && String(v).trim() !== ""))].sort();
    } else {
      billingAccountOpts = filterBillingAccountsByProvider(
        billingAccounts,
        providerSet,
        billingAccountProviderMap,
      );
    }

    // Category and service type options: filter by selected provider (use categoriesByProvider / serviceTypesByProvider or filterCombinations)
    let categoryOpts;
    let serviceTypeOpts;
    const categoriesFromByProvider = getCategoriesForProviders(providerSet, categoriesByProvider);
    const serviceTypesFromByProvider = getServiceTypesForProviders(providerSet, serviceTypesByProvider);
    if (hasProviderFilter && categoriesFromByProvider !== null) {
      categoryOpts = categoriesFromByProvider;
    } else {
      categoryOpts = categories;
    }
    if (hasProviderFilter && serviceTypesFromByProvider !== null) {
      serviceTypeOpts = serviceTypesFromByProvider;
    } else {
      serviceTypeOpts = serviceTypes;
    }

    if (combos.length === 0) {
      return {
        providers: providers,
        categories: hasProviderFilter && categoriesFromByProvider !== null ? categoriesFromByProvider : categories,
        serviceTypes: hasProviderFilter && serviceTypesFromByProvider !== null ? serviceTypesFromByProvider : serviceTypes,
        billingAccounts: hasProviderFilter ? billingAccountOpts : billingAccounts,
      };
    }

    if (hasProviderFilter) {
      combos = combos.filter((c) => c && c.provider && providerSetLc.has(String(c.provider).trim().toLowerCase()));
    }

    // Multi-select: never shrink category/service-type option lists using that field's own selections
    // (same idea as billing accounts — union for provider, narrow only by the *other* dimension).
    let combosForCategoryDropdown = combos;
    if (serviceTypeSet.size > 0) {
      combosForCategoryDropdown = combosForCategoryDropdown.filter(
        (c) => c && c.service_type != null && serviceTypeSet.has(String(c.service_type).trim()),
      );
    }
    let combosForServiceTypeDropdown = combos;
    if (categorySet.size > 0) {
      combosForServiceTypeDropdown = combosForServiceTypeDropdown.filter(
        (c) => c && c.category && categorySet.has(String(c.category).trim()),
      );
    }

    const providerOpts = hasProviderFilter
      ? [...providerSet].sort()
      : [...new Set(combos.map((c) => c.provider).filter(Boolean))].sort();
    if (hasProviderFilter && combos.length > 0) {
      categoryOpts = [...new Set(combosForCategoryDropdown.map((c) => c.category).filter(Boolean))].sort();
      serviceTypeOpts = [...new Set(combosForServiceTypeDropdown.map((c) => c.service_type).filter((v) => v != null && String(v).trim() !== ""))].sort();
    } else if (!hasProviderFilter) {
      categoryOpts = categories;
      serviceTypeOpts = serviceTypes;
    }
    const finalBillingAccounts = hasProviderFilter ? billingAccountOpts : billingAccounts;

    // When a provider is selected, always use the filtered lists (even if empty) — don't fall back to full list
    const finalCategories = hasProviderFilter ? categoryOpts : (categoryOpts.length > 0 ? categoryOpts : categories);
    const finalServiceTypes = hasProviderFilter ? serviceTypeOpts : (serviceTypeOpts.length > 0 ? serviceTypeOpts : serviceTypes);

    return {
      providers: providerOpts.length > 0 ? providerOpts : providers,
      categories: finalCategories,
      serviceTypes: finalServiceTypes,
      billingAccounts: finalBillingAccounts,
    };
  }, [
    filterCombinations,
    billingAccountsByProvider,
    categoriesByProvider,
    serviceTypesByProvider,
    filters.provider,
    filters.category,
    filters.serviceType,
    providers,
    categories,
    serviceTypes,
    billingAccounts,
    billingAccountProviderMap,
  ]);

  // Compute valid options after a filter change (for clearing invalid dependent selections)
  const getValidOptionsAfterChange = (filterName, selectedValues) => {
    const nextProvider = filterName === "provider" ? selectedValues : getSelectedValues("provider");
    const nextCategory = filterName === "category" ? selectedValues : getSelectedValues("category");
    const nextServiceType = filterName === "serviceType" ? selectedValues : getSelectedValues("serviceType");
    const providerSet = toSelectedSet(Array.isArray(nextProvider) ? nextProvider : nextProvider);
    const providerSetLc = providerSetLower(providerSet);

    // Billing accounts: prefer billingAccountsByProvider, then filterCombinations, else map
    let validBillingAccounts;
    const fromByProvider = getBillingAccountsForProviders(providerSet, billingAccountsByProvider);
    if (fromByProvider !== null && providerSet.size > 0) {
      validBillingAccounts = fromByProvider;
    } else if (filterCombinations.length > 0 && providerSet.size > 0) {
      const byProvider = filterCombinations.filter(
        (c) => c && c.provider && providerSetLc.has(String(c.provider).trim().toLowerCase()),
      );
      validBillingAccounts = [...new Set(byProvider.map((c) => c.billing_account).filter((v) => v != null && String(v).trim() !== ""))].sort();
    } else {
      validBillingAccounts = filterBillingAccountsByProvider(
        billingAccounts,
        providerSet,
        billingAccountProviderMap,
      );
    }

    const validCategoriesFromByProvider = getCategoriesForProviders(providerSet, categoriesByProvider);
    const validServiceTypesFromByProvider = getServiceTypesForProviders(providerSet, serviceTypesByProvider);
    if (!filterCombinations.length) {
      const validCategories = providerSet.size > 0 && validCategoriesFromByProvider !== null ? validCategoriesFromByProvider : categories;
      const validServiceTypes = providerSet.size > 0 && validServiceTypesFromByProvider !== null ? validServiceTypesFromByProvider : serviceTypes;
      return { categories: validCategories, serviceTypes: validServiceTypes, billingAccounts: validBillingAccounts };
    }
    const categorySet = toSelectedSet(Array.isArray(nextCategory) ? nextCategory : nextCategory);
    const serviceTypeSet = toSelectedSet(Array.isArray(nextServiceType) ? nextServiceType : nextServiceType);

    let combosByProvider = filterCombinations;
    if (providerSet.size > 0) {
      combosByProvider = combosByProvider.filter(
        (c) => c && c.provider && providerSetLc.has(String(c.provider).trim().toLowerCase()),
      );
    }

    // Valid categories: provider scope + optional service-type filter only (not category selections).
    let combosForValidCategories = combosByProvider;
    if (serviceTypeSet.size > 0) {
      combosForValidCategories = combosForValidCategories.filter(
        (c) => c && c.service_type != null && serviceTypeSet.has(String(c.service_type).trim()),
      );
    }
    const validCategories =
      providerSet.size > 0 && combosForValidCategories.length > 0
        ? [...new Set(combosForValidCategories.map((c) => c.category).filter(Boolean))].sort()
        : providerSet.size > 0 && validCategoriesFromByProvider !== null
          ? validCategoriesFromByProvider
          : categories;

    // Valid service types: provider scope + optional category filter only (not service-type selections).
    let combosForValidServiceTypes = combosByProvider;
    if (categorySet.size > 0) {
      combosForValidServiceTypes = combosForValidServiceTypes.filter(
        (c) => c && c.category && categorySet.has(String(c.category).trim()),
      );
    }
    const validServiceTypes =
      providerSet.size > 0 && combosForValidServiceTypes.length > 0
        ? [...new Set(combosForValidServiceTypes.map((c) => c.service_type).filter((v) => v != null && String(v).trim() !== ""))].sort()
        : providerSet.size > 0 && validServiceTypesFromByProvider !== null
          ? validServiceTypesFromByProvider
          : serviceTypes;
    return { categories: validCategories, serviceTypes: validServiceTypes, billingAccounts: validBillingAccounts };
  };

  // When provider/category/serviceType changes, clear dependent selections that are no longer valid
  const handleMultiSelectChange = (filterName, selectedValues) => {
    const next = { [filterName]: selectedValues };
    const currentCategory = getSelectedValues("category");
    const currentServiceType = getSelectedValues("serviceType");
    const currentBillingAccount = getSelectedValues("billingAccount");
    const valid = getValidOptionsAfterChange(filterName, selectedValues);
    const validCatSet = new Set(valid.categories);
    const validStSet = new Set(valid.serviceTypes);
    const validBaSet = new Set(valid.billingAccounts);

    if (filterName === "provider") {
      next.category = currentCategory.filter((c) => validCatSet.has(c));
      next.serviceType = currentServiceType.filter((s) => validStSet.has(s));
      next.billingAccount = currentBillingAccount.filter((b) => validBaSet.has(b));
    } else if (filterName === "category") {
      next.serviceType = currentServiceType.filter((s) => validStSet.has(s));
      next.billingAccount = currentBillingAccount.filter((b) => validBaSet.has(b));
    } else if (filterName === "serviceType") {
      next.billingAccount = currentBillingAccount.filter((b) => validBaSet.has(b));
    }

    onFilterChange(next);
  };

  // Normalize billing account for lookup (removes whitespace and dashes)
  const normalizeBillingAccount = (account) => {
    if (!account) return "";
    return String(account).trim().replace(/\s+/g, "").replace(/-/g, "");
  };

  // Format billing account display with provider prefix.
  // When exactly one provider is selected, use it for the label so we don't show wrong provider from the map.
  const formatBillingAccountDisplay = (billingAccount) => {
    if (!billingAccount) return billingAccount;

    const accountStr = String(billingAccount).trim();
    const selectedProviders = getSelectedValues("provider");

    if (selectedProviders.length === 1) {
      return `${selectedProviders[0]} - ${accountStr}`;
    }

    let provider = billingAccountProviderMap.get(accountStr);
    if (!provider) {
      const normalizedAccount = normalizeBillingAccount(accountStr);
      provider = billingAccountProviderMap.get(normalizedAccount);
      if (!provider && accountStr.includes(" ")) {
        const singleSpaceNormalized = accountStr.replace(/\s+/g, " ").trim();
        provider = billingAccountProviderMap.get(singleSpaceNormalized);
      }
    }

    if (provider) {
      return `${provider} - ${accountStr}`;
    }

    return accountStr;
  };

  // Use cascading options so dropdowns only show values that apply to current selections
  const sortedCategories = [...cascadingOptions.categories].sort();
  const sortedServiceTypes = [...cascadingOptions.serviceTypes].sort();
  const sortedBillingAccounts = [...cascadingOptions.billingAccounts].sort((a, b) => {
    const displayA = formatBillingAccountDisplay(a).toLowerCase();
    const displayB = formatBillingAccountDisplay(b).toLowerCase();
    return displayA.localeCompare(displayB);
  });

  // Provider list must stay complete when one or more providers are selected; cascadingOptions.providers
  // narrows to only selected values and would block adding additional providers otherwise.
  const providerMultiSelectOptions = useMemo(() => {
    const set = new Set(
      providers.filter((p) => p != null && String(p).trim() !== "" && p !== "Custom"),
    );
    const selected = Array.isArray(filters.provider)
      ? filters.provider
      : filters.provider
        ? [filters.provider]
        : [];
    selected.forEach((p) => {
      if (p != null && String(p).trim() !== "" && p !== "Custom") {
        set.add(String(p).trim());
      }
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [providers, filters.provider]);

  // Filter out "Custom" from categories
  const filteredCategories = sortedCategories.filter((c) => c !== "Custom");

  const gmNameById = useMemo(() => {
    const m = new Map();
    (siteFilterOptions?.generalManagers || []).forEach((gm) => {
      if (gm && gm.id != null) {
        m.set(String(gm.id), gm.name || String(gm.id));
      }
    });
    return m;
  }, [siteFilterOptions]);

  const activeAdvancedSiteFilterCount = useMemo(() => {
    let n = 0;
    if ((filters.siteRegion || []).length) n++;
    if ((filters.siteClassification || []).length) n++;
    if ((filters.siteGeneralManagerId || []).length) n++;
    if ((filters.siteSdWanAppliance || []).length) n++;
    if ((filters.siteStatus || []).length) n++;
    if ((filters.circuitAction || []).length) n++;
    if (
      filters.contractEndsWithin != null &&
      String(filters.contractEndsWithin).trim() !== ""
    ) {
      n++;
    }
    return n;
  }, [
    filters.siteRegion,
    filters.siteClassification,
    filters.siteGeneralManagerId,
    filters.siteSdWanAppliance,
    filters.siteStatus,
    filters.circuitAction,
    filters.contractEndsWithin,
  ]);

  const regionOptions = siteFilterOptions?.regions ?? [];
  const classificationOptions = siteFilterOptions?.siteClassifications ?? [];
  const gmIdOptions = (siteFilterOptions?.generalManagers || []).map((gm) =>
    String(gm.id),
  );
  const sdWanOptions = siteFilterOptions?.sdWanApplianceNames ?? [];
  const siteStatusOptions = siteFilterOptions?.siteStatuses ?? [];

  const handleSiteMultiSelectChange = (filterKey, selectedValues) => {
    onFilterChange({ [filterKey]: selectedValues });
  };

  const filterLabelClass =
    "block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1";
  const sortBy = sortConfig?.sortBy || "address";
  const sortOrder = sortConfig?.sortOrder || "asc";

  return (
    <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
      <div className="flex flex-col xl:flex-row xl:items-end gap-3">
        <div className="flex-1 min-w-0">
          <label className={filterLabelClass}>Search</label>
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--faint,#9a9a95)]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
            <input
              type="search"
              name="search"
              placeholder="Circuit ID, billing account, address or note:"
              value={searchInput}
              onChange={handleSearchChange}
              className="h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white pl-8 pr-3 text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--faint,#9a9a95)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]"
            />
          </div>
        </div>
        <div className="w-full xl:w-[160px]">
          <label className={filterLabelClass}>Provider</label>
          <CustomMultiSelect
            options={providerMultiSelectOptions}
            value={getSelectedValues("provider")}
            onChange={(selectedValues) =>
              handleMultiSelectChange("provider", selectedValues)
            }
            placeholder="All"
            maxVisibleChips={1}
            searchable
            searchPlaceholder="Type to filter providers…"
          />
        </div>
        <div className="w-full xl:w-[140px]">
          <label className={filterLabelClass}>Category</label>
          <CustomMultiSelect
            options={filteredCategories}
            value={getSelectedValues("category")}
            onChange={(selectedValues) =>
              handleMultiSelectChange("category", selectedValues)
            }
            placeholder="All"
            maxVisibleChips={1}
            searchable
            searchPlaceholder="Type to filter categories…"
          />
        </div>
        <div className="w-full xl:w-[150px]">
          <label className={filterLabelClass}>Service type</label>
          <CustomMultiSelect
            options={sortedServiceTypes}
            value={getSelectedValues("serviceType")}
            onChange={(selectedValues) =>
              handleMultiSelectChange("serviceType", selectedValues)
            }
            placeholder="All"
            maxVisibleChips={1}
            searchable
            searchPlaceholder="Type to filter service types…"
          />
        </div>
        <div className="w-full xl:w-[170px]">
          <label className={filterLabelClass}>Billing account</label>
          <CustomMultiSelect
            options={sortedBillingAccounts}
            value={getSelectedValues("billingAccount")}
            onChange={(selectedValues) =>
              handleMultiSelectChange("billingAccount", selectedValues)
            }
            placeholder="All"
            maxVisibleChips={1}
            displayFormatter={formatBillingAccountDisplay}
            searchable
            searchPlaceholder="Type to filter accounts…"
          />
        </div>
        <div className="w-full xl:w-auto flex-shrink-0">
          <label className={`${filterLabelClass} hidden xl:block`}>&nbsp;</label>
          <button
            type="button"
            onClick={() => setAdvancedSiteSearchOpen((o) => !o)}
            className="inline-flex h-[38px] w-full xl:w-auto items-center justify-center gap-1.5 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
            aria-expanded={advancedSiteSearchOpen}
          >
            Advanced site search
            <ChevronDownIcon
              className={`w-4 h-4 text-[var(--muted,#6b6a66)] transition-transform ${
                advancedSiteSearchOpen ? "rotate-180" : ""
              }`}
            />
            {activeAdvancedSiteFilterCount > 0 && (
              <span className="text-[12px] font-medium text-[var(--muted,#6b6a66)]">
                ({activeAdvancedSiteFilterCount})
              </span>
            )}
          </button>
        </div>
      </div>

      {advancedSiteSearchOpen && (
        <div className="mt-3 pt-3 border-t border-[var(--line,#e4e2dc)] space-y-3">
          {siteFilterOptionsLoading && !siteFilterOptions ? (
            <p className="text-[13px] text-[var(--muted,#6b6a66)] py-2">
              Loading site filter options…
            </p>
          ) : (
            <>
              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 items-start">
                <div className="min-w-0 w-full">
                  <label className={filterLabelClass}>Region</label>
                  <CustomMultiSelect
                    options={regionOptions}
                    value={
                      Array.isArray(filters.siteRegion) ? filters.siteRegion : []
                    }
                    onChange={(v) => handleSiteMultiSelectChange("siteRegion", v)}
                    placeholder="All"
                    maxVisibleChips={1}
                  />
                </div>
                <div className="min-w-0 w-full">
                  <label className={filterLabelClass}>Site classification</label>
                  <CustomMultiSelect
                    options={classificationOptions}
                    value={
                      Array.isArray(filters.siteClassification)
                        ? filters.siteClassification
                        : []
                    }
                    onChange={(v) =>
                      handleSiteMultiSelectChange("siteClassification", v)
                    }
                    placeholder="All"
                    maxVisibleChips={1}
                  />
                </div>
                <div className="min-w-0 w-full">
                  <label className={filterLabelClass}>General manager</label>
                  <CustomMultiSelect
                    options={gmIdOptions}
                    value={
                      Array.isArray(filters.siteGeneralManagerId)
                        ? filters.siteGeneralManagerId.map(String)
                        : []
                    }
                    onChange={(v) =>
                      handleSiteMultiSelectChange("siteGeneralManagerId", v)
                    }
                    placeholder="All"
                    maxVisibleChips={1}
                    displayFormatter={(id) => gmNameById.get(String(id)) || id}
                  />
                </div>
                <div className="min-w-0 w-full">
                  <label className={filterLabelClass}>SD WAN appliance</label>
                  <CustomMultiSelect
                    options={sdWanOptions}
                    value={
                      Array.isArray(filters.siteSdWanAppliance)
                        ? filters.siteSdWanAppliance
                        : []
                    }
                    onChange={(v) =>
                      handleSiteMultiSelectChange("siteSdWanAppliance", v)
                    }
                    placeholder="All"
                    maxVisibleChips={1}
                  />
                </div>
                <div className="min-w-0 w-full">
                  <label className={filterLabelClass}>Site status</label>
                  <CustomMultiSelect
                    options={siteStatusOptions}
                    value={
                      Array.isArray(filters.siteStatus) ? filters.siteStatus : []
                    }
                    onChange={(v) => handleSiteMultiSelectChange("siteStatus", v)}
                    placeholder="All"
                    maxVisibleChips={1}
                  />
                </div>
                <div className="min-w-0 w-full">
                  <label className={filterLabelClass}>Action</label>
                  <CustomMultiSelect
                    options={INVENTORY_ACTION_OPTIONS}
                    value={
                      Array.isArray(filters.circuitAction)
                        ? filters.circuitAction
                        : []
                    }
                    onChange={(v) =>
                      handleSiteMultiSelectChange("circuitAction", v)
                    }
                    placeholder="All"
                    maxVisibleChips={1}
                  />
                </div>
                <div className="min-w-0 w-full">
                  <label className={filterLabelClass}>Contract ends within</label>
                  <CustomSelect
                    value={
                      contractEndsCustomMode
                        ? "custom"
                        : contractEndsRaw &&
                            CONTRACT_ENDS_PRESET_VALUES.has(contractEndsRaw)
                          ? contractEndsRaw
                          : ""
                    }
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === "custom") {
                        setContractEndsCustomMode(true);
                        if (customDaysDraft.trim()) {
                          applyCustomContractDays(customDaysDraft);
                        } else {
                          onFilterChange({ contractEndsWithin: "" });
                        }
                        return;
                      }
                      setContractEndsCustomMode(false);
                      setCustomDaysDraft("");
                      setCustomEndsByDraft("");
                      onFilterChange({ contractEndsWithin: next });
                    }}
                    options={contractEndsSelectOptions}
                    className="h-[38px]"
                    aria-label="Contract ends within"
                  />
                </div>
              </div>

              {contractEndsCustomMode ? (
                <div className="flex flex-wrap items-end gap-x-3 gap-y-2 rounded-md border border-[var(--line,#e4e2dc)] bg-[var(--sunken,#f7f6f3)] px-3 py-2.5">
                  <div className="w-[110px] min-w-0">
                    <label className={filterLabelClass}>Days</label>
                    <input
                      type="number"
                      min={1}
                      max={CONTRACT_ENDS_MAX_DAYS}
                      inputMode="numeric"
                      value={customDaysDraft}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^\d]/g, "");
                        setCustomDaysDraft(v);
                        const days = Number.parseInt(v, 10);
                        if (
                          Number.isFinite(days) &&
                          days >= 1 &&
                          days <= CONTRACT_ENDS_MAX_DAYS
                        ) {
                          setCustomEndsByDraft(dateFromDays(String(days)));
                        } else {
                          setCustomEndsByDraft("");
                        }
                      }}
                      placeholder="e.g. 45"
                      className={customInputClass}
                      aria-label="Custom contract ends within days"
                    />
                  </div>
                  <span className="pb-2 text-[12px] text-[var(--muted,#6b6a66)]">
                    or
                  </span>
                  <div className="w-[168px] min-w-0">
                    <label className={filterLabelClass}>Ends by</label>
                    <input
                      type="date"
                      value={customEndsByDraft}
                      min={dateFromDays("1")}
                      max={dateFromDays(String(CONTRACT_ENDS_MAX_DAYS))}
                      onChange={(e) => {
                        const dateVal = e.target.value;
                        setCustomEndsByDraft(dateVal);
                        const days = daysUntilDate(dateVal);
                        if (days) {
                          setCustomDaysDraft(days);
                        } else if (!dateVal) {
                          setCustomDaysDraft("");
                        }
                      }}
                      className={customInputClass}
                      aria-label="Contract ends by date"
                    />
                  </div>
                  <p className="pb-2 text-[12px] text-[var(--muted,#6b6a66)]">
                    Same filter — pick days or an end date
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      {totalCount != null && (
        <p className="mt-2 text-right text-[12px] text-[var(--muted,#6b6a66)]">
          Showing {Number(shownCount ?? 0).toLocaleString()} of{" "}
          {Number(totalCount || 0).toLocaleString()}{" "}
          {Number(totalCount || 0) === 1 ? "address" : "addresses"} · sorted by{" "}
          {sortBy} {sortOrder}
        </p>
      )}
    </div>
  );
};

export default FilterBar;
